import { parseExpression } from '../../lscript/parser';
import { visitSubexpressions } from '../../lscript/utils/visitSubexpressions';
import type { CalculationMethodVariable } from './calculationMethodVariables';

/**
 * Extracts variable names (identifiers) from an expression string.
 * Uses the lscript parser to properly parse the expression and find all identifier nodes.
 *
 * @param expression The expression string to parse (undefined/empty returns empty set)
 * @returns A set of variable names found in the expression
 */
export function extractVariableNamesFromExpression(
  expression: string | undefined
): Set<string> {
  if (!expression) {
    return new Set();
  }

  try {
    const ast = parseExpression(expression);
    const names = new Set<string>();

    for (const node of visitSubexpressions(ast)) {
      if (node.t === 'identifier') {
        names.add(node.name);
      }
    }

    return names;
  } catch {
    // Graceful degradation on parse errors - return empty set
    return new Set();
  }
}

/**
 * Orders variables by placing directly-used variables first, followed by transitive variables.
 * Preserves the original ordering within each bucket for stability.
 *
 * @param variables The variables to sort
 * @param directVariableNames Optional set of directly-used variable names. If not provided, returns variables unchanged.
 * @returns The sorted array with direct variables first, then transitive variables
 */
export function sortVariablesByDirectUse({
  variables,
  directVariableNames,
}: {
  variables: Array<CalculationMethodVariable>;
  directVariableNames?: Set<string>;
}): Array<CalculationMethodVariable> {
  if (!directVariableNames) {
    return variables;
  }

  const direct: Array<CalculationMethodVariable> = [];
  const transitive: Array<CalculationMethodVariable> = [];

  for (const v of variables) {
    (directVariableNames.has(v.name) ? direct : transitive).push(v);
  }

  return [...direct, ...transitive];
}
